import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { Row, Col, Input, FormGroup, FormFeedback } from "reactstrap";

import { Btn } from "../../../../AbstractElements";
import FilterSliderModal from "../../../../CommonElements/FilterSliderModal";
import FixedFooter from "../../../../CommonElements/FixedFooter";
import LabelTooltip from "../../../../CommonElements/LabelTooltip";
import SearchDropDownPaginator from "../../../../CommonElements/SearchDropDownPaginator";
import CheckboxButton from "../../../../CommonElements/SwitchButton";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import { getCourseList } from "../../../../ReduxToolkit/Reducers/CourseSlice";
import { getDomainList as getMasterDomainList } from "../../../../ReduxToolkit/Reducers/MasterSlice";
import {
  SurveyTitle,
  SurveyTitleTooltip,
  SurveyDescription,
  SurveyDescriptionTooltip,
  MarkMultipleSurveyTooltip,
  CourseFieldTooltip,
  CourseDurationTooltip,
  ResubmissionLimitTooltip,
  ResubmissionLimit,
  Course,
  DurationDays,
  AfterHowManyDaysToAddSurvey,
  MarkMultipleSurvey,
  SurveyTitleRequired,
  SurveyDescriptionRequired,
  SurveyCourseRequired,
  SurveyDurationGreaterThanZero,
  SurveyDaysAfterJoiningGreaterThanZero,
  SurveyResubmissionLimitGreaterThanZero,
} from "../../../../utils/Constant";
import { Close, Next } from "../../../../utils/Constant";
import { handleKeyDownOnNumericWithoutDecimal } from "../../../../utils/helper/helper";
import { customStyles } from "../../../../utils/helper/helper";
import {
  courseFilterDropdownList,
  mapListToOptions,
} from "../../../../utils/helper/helper";

const surveyForOptions = [
  { label: "Select", value: "" },
  { label: "Course", value: "course" },
  { label: "Employee onboard", value: "employee_onboard" },
];

const FirstStep = ({ form, setForm, errors, setErrors, onNext }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { courseList } = useAppSelector(state => state.course);
  const { domainList } = useAppSelector(state => state.master);
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [domainListData, setDomainListData] = useState([]);
  const [courseOptions, setCourseOptions] = useState([
    { label: "Select", value: "" },
  ]);

  useEffect(() => {
    dispatch(
      getCourseList({ status: ["published"], course_type: [], domain: null })
    );
    if (!domainList || !domainList.domain || domainList.domain.length === 0) {
      dispatch(getMasterDomainList());
    }
  }, [dispatch]);

  useEffect(() => {
    if (domainList && domainList.domain) {
      setDomainListData(
        mapListToOptions(domainList.domain, "domain_name", "id")
      );
    }
  }, [domainList]);

  useEffect(() => {
    setCourseOptions([
      { label: "Select", value: "" },
      ...(courseList?.course_list?.map(item => ({
        value: String(item.id),
        label: item.course_name,
      })) || []),
    ]);
  }, [courseList]);

  const handleFilterDone = async values => {
    setForm(prev => ({ ...prev, courseId: "" }));
    const { course_type = [], domain = [] } = values;
    await dispatch(
      getCourseList({
        status: ["published"],
        course_type: course_type.length > 0 ? course_type : [],
        domain: domain.length > 0 ? domain : null,
      })
    );
  };

  const handleFilterClear = async () => {
    setForm(prev => ({ ...prev, courseId: "" }));
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    await dispatch(
      getCourseList({ status: ["published"], course_type: [], domain: null })
    );
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // For react-select and custom controls
  const handleCustomChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.title || form.title.trim() === "")
      newErrors.title = SurveyTitleRequired;
    if (!form.description || form.description.trim() === "")
      newErrors.description = SurveyDescriptionRequired;
    if (!form.surveyFor) newErrors.surveyFor = "Survey for is required";

    if (form.surveyFor === "course") {
      if (!form.courseId) newErrors.courseId = SurveyCourseRequired;
      if (!form.courseDuration || Number(form.courseDuration) <= 0)
        newErrors.courseDuration = SurveyDurationGreaterThanZero;
    } else if (form.surveyFor === "employee_onboard") {
      if (
        form.onboarding_survey_offset === undefined ||
        form.onboarding_survey_offset === null ||
        form.onboarding_survey_offset === "" ||
        Number(form.onboarding_survey_offset) <= 0
      ) {
        newErrors.onboarding_survey_offset =
          SurveyDaysAfterJoiningGreaterThanZero;
      }
      if (!form.courseDuration || Number(form.courseDuration) <= 0)
        newErrors.courseDuration = SurveyDurationGreaterThanZero;
    }
    if (form.markMultiple) {
      if (!form.resubmissionLimit || Number(form.resubmissionLimit) <= 0)
        newErrors.resubmissionLimit = SurveyResubmissionLimitGreaterThanZero;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <>
      <Row className="mb-3">
        <Col lg={12} md={12} sm={12} xs={12}>
          <FormGroup>
            <LabelTooltip
              label={SurveyTitle}
              tooltipText={SurveyTitleTooltip}
              important
              forHtml="title"
            />
            <Input
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter survey title"
              maxLength={100}
              invalid={!!errors.title}
            />
            {errors.title && <FormFeedback>{errors.title}</FormFeedback>}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <LabelTooltip
              label={SurveyDescription}
              tooltipText={SurveyDescriptionTooltip}
              important
              forHtml="description"
            />
            <Input
              id="description"
              name="description"
              type="textarea"
              value={form.description}
              onChange={handleChange}
              placeholder="Enter description"
              maxLength={256}
              invalid={!!errors.description}
            />
            {errors.description && (
              <FormFeedback>{errors.description}</FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={6} md={12} sm={12} xs={12}>
          <FormGroup>
            <LabelTooltip
              label="Survey for"
              tooltipText="Select the type of survey"
              forHtml="surveyFor"
              important
            />
            <ReactSelect
              name="surveyFor"
              className="text-gray"
              value={
                surveyForOptions.find(
                  option => option.value === form.surveyFor
                ) || null
              }
              onChange={selectedOption =>
                handleCustomChange(
                  "surveyFor",
                  selectedOption ? selectedOption.value : ""
                )
              }
              options={surveyForOptions}
              isSearchable={false}
              isClearable={false}
              styles={customStyles}
            />
            {errors.surveyFor && (
              <FormFeedback className="d-block">
                {errors.surveyFor}
              </FormFeedback>
            )}
          </FormGroup>
          {form.surveyFor === "course" && (
            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <Row>
                  <Col>
                    <FormGroup>
                      <LabelTooltip
                        label={Course}
                        tooltipText={CourseFieldTooltip}
                        forHtml="courseId"
                        important
                      />
                      <ReactSelect
                        name="courseId"
                        className="text-gray"
                        value={
                          courseOptions.find(
                            option => option.value === form.courseId
                          ) || null
                        }
                        onChange={selectedOption =>
                          handleCustomChange(
                            "courseId",
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        options={courseOptions}
                        isSearchable
                        isClearable={false}
                        styles={customStyles}
                        components={{
                          MenuList: props => (
                            <SearchDropDownPaginator
                              {...props}
                              maxHeight={250}
                            />
                          ),
                        }}
                        filterOption={(option, inputValue) =>
                          option?.label
                            ?.toLowerCase()
                            ?.includes(inputValue?.toLowerCase())
                        }
                      />
                      {errors.courseId && (
                        <FormFeedback className="d-block">
                          {errors.courseId}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col lg="1">
                    <div className="filter-margin">
                      <FilterSliderModal
                        dropdowns={[
                          {
                            label: "Course Type",
                            key: "course_type",
                            tooltipText: "Select Course Type",
                            options: courseFilterDropdownList,
                            isMultiSelect: true,
                          },
                          {
                            label: "Domain",
                            key: "domain",
                            tooltipText: "Select Domain",
                            options: domainListData,
                            isMultiSelect: true,
                          },
                        ]}
                        selectedFilterValues={selectedFilterValues}
                        defaultFilterValues={defaultFilterValues}
                        setSelectedFilterValues={setSelectedFilterValues}
                        setDefaultFilterValues={setDefaultFilterValues}
                        onDone={handleFilterDone}
                        onClear={handleFilterClear}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col>
                <FormGroup>
                  <LabelTooltip
                    label={DurationDays}
                    tooltipText={CourseDurationTooltip}
                    forHtml="courseDuration"
                    important
                  />
                  <Input
                    type="number"
                    name="courseDuration"
                    id="courseDuration"
                    value={form.courseDuration}
                    onChange={handleChange}
                    placeholder="Enter duration"
                    min={1}
                    max={1000}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    invalid={!!errors.courseDuration}
                  />
                  {errors.courseDuration && (
                    <FormFeedback className="d-block">
                      {errors.courseDuration}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>
          )}
          {form.surveyFor === "employee_onboard" && (
            <Row>
              <Col>
                <FormGroup>
                  <LabelTooltip
                    label={AfterHowManyDaysToAddSurvey}
                    tooltipText={AfterHowManyDaysToAddSurvey}
                    forHtml="onboarding_survey_offset"
                    important
                  />
                  <Input
                    type="number"
                    name="onboarding_survey_offset"
                    id="onboarding_survey_offset"
                    value={form.onboarding_survey_offset}
                    onChange={handleChange}
                    placeholder="Days after joining"
                    min={0}
                    max={1000}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    invalid={!!errors.onboarding_survey_offset}
                  />
                  {errors.onboarding_survey_offset && (
                    <FormFeedback className="d-block">
                      {errors.onboarding_survey_offset}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <LabelTooltip
                    label={DurationDays}
                    tooltipText={CourseDurationTooltip}
                    forHtml="courseDuration"
                    important
                  />
                  <Input
                    type="number"
                    name="courseDuration"
                    id="courseDuration"
                    value={form.courseDuration}
                    onChange={handleChange}
                    placeholder="Enter duration"
                    min={1}
                    max={1000}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    invalid={!!errors.courseDuration}
                  />
                  {errors.courseDuration && (
                    <FormFeedback className="d-block">
                      {errors.courseDuration}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>
          )}
        </Col>
        <Col className="mt-1">
          <FormGroup>
            <LabelTooltip
              label={MarkMultipleSurvey}
              tooltipText={MarkMultipleSurveyTooltip}
              forHtml="markMultiple"
              important={false}
            />
            <CheckboxButton
              checked={form.markMultiple}
              onChange={() =>
                handleChange({
                  target: {
                    name: "markMultiple",
                    type: "checkbox",
                    checked: !form.markMultiple,
                  },
                })
              }
            />
          </FormGroup>
          {form.markMultiple && (
            <FormGroup className="mt-2">
              <LabelTooltip
                label={ResubmissionLimit}
                tooltipText={ResubmissionLimitTooltip}
                forHtml="resubmissionLimit"
                important
              />
              <Input
                type="number"
                name="resubmissionLimit"
                id="resubmissionLimit"
                value={form.resubmissionLimit}
                onChange={handleChange}
                placeholder="Enter limit"
                min={1}
                max={1000}
                onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                invalid={!!errors.resubmissionLimit}
              />
              {errors.resubmissionLimit && (
                <FormFeedback className="d-block">
                  {errors.resubmissionLimit}
                </FormFeedback>
              )}
            </FormGroup>
          )}
        </Col>
      </Row>
      <FixedFooter>
        <div className="d-flex justify-content-between gap-2">
          <Btn
            outline
            className="alert-light-primary"
            color="primary"
            onClick={() => navigate("/manage-surveys")}
          >
            {Close}
          </Btn>
          <Btn
            color="primary"
            onClick={() => {
              if (validate()) onNext(getApiPayload(form));
            }}
          >
            {Next}
          </Btn>
        </div>
      </FixedFooter>
    </>
  );
};

FirstStep.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export const getApiPayload = form => {
  const payload: any = {
    is_multiple_submissions: !!form.markMultiple,
  };
  if (form.surveyFor === "course") {
    payload.course_id = form.courseId ? Number(form.courseId) : undefined;
    payload.duration = form.courseDuration
      ? Number(form.courseDuration)
      : undefined;
  } else if (form.surveyFor === "employee_onboard") {
    payload.onboarding_survey_offset = form.onboarding_survey_offset
      ? Number(form.onboarding_survey_offset)
      : undefined;
    payload.duration = form.courseDuration
      ? Number(form.courseDuration)
      : undefined;
  }
  if (form.markMultiple) {
    payload.resubmission_limit = form.resubmissionLimit
      ? Number(form.resubmissionLimit)
      : undefined;
  }
  return payload;
};

export const prefillFormFromApiResponse = (apiResponse, setForm) => {
  const {
    course_id,
    duration,
    onboarding_survey_offset,
    is_multiple_submissions,
    resubmission_limit,
  } = apiResponse;

  let surveyFor = "";
  if (course_id != null && course_id !== "") {
    surveyFor = "course";
  } else if (
    onboarding_survey_offset != null &&
    onboarding_survey_offset !== ""
  ) {
    surveyFor = "employee_onboard";
  }

  setForm(prev => ({
    ...prev,
    surveyFor,
    courseId: course_id != null && course_id !== "" ? String(course_id) : "",
    courseDuration: duration != null && duration !== "" ? String(duration) : "",
    onboarding_survey_offset:
      onboarding_survey_offset != null && onboarding_survey_offset !== ""
        ? String(onboarding_survey_offset)
        : "",
    markMultiple: !!is_multiple_submissions,
    resubmissionLimit:
      resubmission_limit != null && resubmission_limit !== ""
        ? String(resubmission_limit)
        : "",
  }));
};

export default FirstStep;
